import { Edit, Save } from "@mui/icons-material";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";

interface EditableDateProps {
  value?: string;
  onUpdate: (value: string | null) => Promise<void>;
}

export const EditableDate = ({ value, onUpdate }: EditableDateProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState<string>(value ?? "");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSave = async () => {
    if (isUpdating) {
      return;
    }

    setIsUpdating(true);
    try {
      await onUpdate(tempValue || null);
      setIsEditing(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) {
      return "Not set";
    }
    return DateTime.fromISO(dateStr).toLocaleString(DateTime.DATE_SHORT);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // The date input gives us YYYY-MM-DD, convert to midnight UTC
    const date = DateTime.fromISO(e.target.value).setZone("UTC").startOf("day");
    setTempValue(date?.toISO() ?? "");
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {isEditing ? (
        <>
          <TextField
            type="date"
            value={tempValue ? DateTime.fromISO(tempValue).toFormat("yyyy-MM-dd") : ""}
            onChange={handleInputChange}
            size="small"
            disabled={isUpdating}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSave();
              }
            }}
            label="Date"
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
          <IconButton onClick={handleSave} size="small" disabled={isUpdating} aria-label="Save date">
            <Save />
          </IconButton>
        </>
      ) : (
        <>
          <Typography variant="h6">{formatDate(value)}</Typography>
          <IconButton onClick={() => setIsEditing(true)} size="small" aria-label="Edit date">
            <Edit />
          </IconButton>
        </>
      )}
    </Box>
  );
};
